
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

interface PaymentPlan {
  id: number;
  name: string;
  students: string;
  price: string;
  numericPrice: number;
}

export default function PagoPage() {
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan as PaymentPlan | null;
  
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [formData, setFormData] = useState({
    institutionName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    studentCount: '',
    selectedPlan: selectedPlan?.name || '',
    amount: selectedPlan?.price || '',
    paymentMethod: 'transfer',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (selectedPlan) {
      setFormData(prev => ({
        ...prev,
        selectedPlan: selectedPlan.name,
        amount: selectedPlan.price
      }));
    }
  }, [selectedPlan]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3avkc8g3i95tm0o9jcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          institutionName: '',
          contactName: '',
          email: '',
          phone: '',
          address: '',
          studentCount: '',
          selectedPlan: '',
          amount: '',
          paymentMethod: 'transfer',
          specialRequests: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const paymentMethods = [
    {
      id: 'transfer',
      name: 'Transferencia Bancaria SPEI',
      icon: 'ri-bank-line',
      recommended: true,
      description: 'Método recomendado - Procesamiento inmediato'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: 'ri-paypal-line',
      recommended: false,
      description: 'Pago seguro internacional'
    },
    {
      id: 'debit',
      name: 'Tarjeta de Débito',
      icon: 'ri-bank-card-line',
      recommended: false,
      description: 'Pago con tarjeta bancaria'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-700 text-white py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Procesar Pago</h1>
            <p className="text-base sm:text-lg md:text-xl opacity-90 mb-4 sm:mb-2">Curso de Inteligencia Artificial - 24 Módulos</p>
            {selectedPlan && (
              <div className="bg-gradient-to-r from-cyan-400/20 to-blue-500/20 backdrop-blur-sm rounded-xl p-3 sm:p-4 max-w-md mx-auto border border-cyan-300/30">
                <p className="font-semibold text-sm sm:text-base">{selectedPlan.name}</p>
                <p className="text-xs sm:text-sm opacity-90">{selectedPlan.students}</p>
                <p className="text-xl sm:text-2xl font-bold mt-2">{selectedPlan.price} MXN</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Formulario de Pago */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 border border-white/20">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center">
                <i className="ri-file-text-line mr-2 sm:mr-3 text-cyan-400"></i>
                Información de Facturación
              </h2>

              <form onSubmit={handleSubmit} data-readdy-form id="pago-curso-ia">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-sm font-bold text-white mb-2">
                      Nombre de la Institución *
                    </label>
                    <input
                      type="text"
                      name="institutionName"
                      value={formData.institutionName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                      placeholder="Ej: Colegio San Patricio"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-white mb-2">
                      Nombre del Contacto *
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                      placeholder="Nombre completo del responsable"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-white mb-2">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                      placeholder="email@institucion.edu"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-white mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                      placeholder="+52 55 1234 5678"
                    />
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-bold text-white mb-2">
                    Dirección de la Institución *
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                    placeholder="Dirección completa de la institución"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-sm font-bold text-white mb-2">
                      Número de Estudiantes *
                    </label>
                    <input
                      type="number"
                      name="studentCount"
                      value={formData.studentCount}
                      onChange={handleInputChange}
                      required
                      min="1"
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                      placeholder="Cantidad de estudiantes"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-white mb-2">
                      Plan Seleccionado
                    </label>
                    <input
                      type="text"
                      name="selectedPlan"
                      value={formData.selectedPlan}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg bg-white/5 text-sm text-white placeholder-blue-200"
                      placeholder="Selecciona un plan"
                      readOnly
                    />
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-bold text-white mb-2">
                    Método de Pago *
                  </label>
                  <div className="grid gap-3 sm:gap-4">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className={`relative border-2 rounded-xl p-3 sm:p-4 cursor-pointer transition-all duration-300 backdrop-blur-sm ${
                          paymentMethod === method.id
                            ? 'border-cyan-400 bg-cyan-400/20'
                            : 'border-white/20 hover:border-white/30 bg-white/5'
                        }`}
                        onClick={() => {
                          setPaymentMethod(method.id);
                          setFormData(prev => ({ ...prev, paymentMethod: method.id }));
                        }}
                      >
                        {method.recommended && (
                          <div className="absolute -top-2 left-4 bg-gradient-to-r from-green-400 to-cyan-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs font-semibold">
                            Recomendado
                          </div>
                        )}
                        <div className="flex items-center">
                          <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 mr-3 sm:mr-4 flex items-center justify-center flex-shrink-0 ${
                            paymentMethod === method.id ? 'border-cyan-400 bg-cyan-400' : 'border-white/30'
                          }`}>
                            {paymentMethod === method.id && <div className="w-2 h-2 sm:w-3 sm:h-3 bg-white rounded-full"></div>}
                          </div>
                          <i className={`${method.icon} text-xl sm:text-2xl mr-3 sm:mr-4 text-cyan-300 flex-shrink-0`}></i>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-white text-sm sm:text-base">{method.name}</h3>
                            <p className="text-xs sm:text-sm text-blue-200">{method.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-bold text-white mb-2">
                    Solicitudes Especiales
                  </label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-cyan-300/30 rounded-lg focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-blue-200"
                    placeholder="Cualquier solicitud especial o comentario adicional (máximo 500 caracteres)"
                  />
                  <p className="text-xs text-blue-200 mt-1">
                    {formData.specialRequests.length}/500 caracteres
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-4 sm:mb-6 bg-gradient-to-r from-green-400/20 to-cyan-500/20 backdrop-blur-sm border border-green-300/30 rounded-lg p-4">
                    <div className="flex items-center text-green-200">
                      <i className="ri-check-circle-line mr-2 text-lg sm:text-xl flex-shrink-0"></i>
                      <span className="font-semibold text-sm sm:text-base">¡Solicitud enviada exitosamente!</span>
                    </div>
                    <p className="text-green-300 text-xs sm:text-sm mt-1">
                      Nos pondremos en contacto contigo pronto con los detalles de pago.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-4 sm:mb-6 bg-gradient-to-r from-red-400/20 to-orange-500/20 backdrop-blur-sm border border-red-300/30 rounded-lg p-4">
                    <div className="flex items-center text-red-200">
                      <i className="ri-error-warning-line mr-2 text-lg sm:text-xl flex-shrink-0"></i>
                      <span className="font-semibold text-sm sm:text-base">Error al enviar la solicitud</span>
                    </div>
                    <p className="text-red-300 text-xs sm:text-sm mt-1">
                      Por favor, inténtalo de nuevo o contáctanos directamente.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-lg text-base sm:text-lg font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Procesando...' : 'Enviar Solicitud de Pago'}
                </button>
              </form>
            </div>
          </div>

          {/* Información de Pago */}
          <div className="space-y-4 sm:space-y-6">
            {/* Resumen */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-6 border border-white/20">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Resumen del Pedido</h3>
              {selectedPlan ? (
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between">
                    <span className="text-blue-200 text-sm sm:text-base">Plan:</span>
                    <span className="font-semibold text-white text-sm sm:text-base">{selectedPlan.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-200 text-sm sm:text-base">Estudiantes:</span>
                    <span className="font-semibold text-white text-sm sm:text-base">{selectedPlan.students}</span>
                  </div>
                  <div className="border-t border-white/20 pt-2 sm:pt-3">
                    <div className="flex justify-between text-base sm:text-lg">
                      <span className="font-bold text-white">Total:</span>
                      <span className="font-bold text-cyan-400">{selectedPlan.price} MXN</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-blue-200 text-sm sm:text-base">Selecciona un plan para ver el resumen</p>
              )}
            </div>

            {/* Detalles de Pago por Método */}
            {paymentMethod === 'transfer' && (
              <div className="bg-gradient-to-br from-green-400/20 to-cyan-500/20 backdrop-blur-sm border border-green-300/30 rounded-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-green-200 mb-3 sm:mb-4 flex items-center">
                  <i className="ri-bank-line mr-2"></i>
                  Transferencia Bancaria SPEI
                </h3>
                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                  <div>
                    <span className="font-semibold text-green-300">Banco:</span>
                    <span className="ml-2 text-white">BANCOPPEL</span>
                  </div>
                  <div>
                    <span className="font-semibold text-green-300">CLABE:</span>
                    <span className="ml-2 font-mono text-white break-all">137180103202403153</span>
                  </div>
                  <div>
                    <span className="font-semibold text-green-300">Beneficiario:</span>
                    <span className="ml-2 text-white">Israel Bertotti Santillan</span>
                  </div>
                  <div className="bg-green-400/20 backdrop-blur-sm p-2 sm:p-3 rounded-lg mt-3 sm:mt-4 border border-green-300/30">
                    <p className="text-green-200 text-xs">
                      <i className="ri-information-line mr-1"></i>
                      Método recomendado. Una vez enviada la solicitud, recibirás los detalles exactos para realizar la transferencia.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {paymentMethod === 'paypal' && (
              <div className="bg-gradient-to-br from-blue-400/20 to-purple-500/20 backdrop-blur-sm border border-blue-300/30 rounded-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-blue-200 mb-3 sm:mb-4 flex items-center">
                  <i className="ri-paypal-line mr-2"></i>
                  PayPal
                </h3>
                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                  <div>
                    <span className="font-semibold text-blue-300">Email PayPal:</span>
                    <span className="ml-2 text-white break-all">israel.bertotti@example.com</span>
                  </div>
                  <div className="bg-blue-400/20 backdrop-blur-sm p-2 sm:p-3 rounded-lg mt-3 sm:mt-4 border border-blue-300/30">
                    <p className="text-blue-200 text-xs">
                      <i className="ri-information-line mr-1"></i>
                      Te enviaremos una solicitud de pago a través de PayPal una vez procesada tu solicitud.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {paymentMethod === 'debit' && (
              <div className="bg-gradient-to-br from-purple-400/20 to-pink-500/20 backdrop-blur-sm border border-purple-300/30 rounded-2xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-purple-200 mb-3 sm:mb-4 flex items-center">
                  <i className="ri-bank-card-line mr-2"></i>
                  Tarjeta de Débito
                </h3>
                <div className="bg-purple-400/20 backdrop-blur-sm p-2 sm:p-3 rounded-lg border border-purple-300/30">
                  <p className="text-purple-200 text-xs">
                    <i className="ri-information-line mr-1"></i>
                    Te proporcionaremos un enlace seguro para realizar el pago con tarjeta una vez procesada tu solicitud.
                  </p>
                </div>
              </div>
            )}

            {/* Garantías */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl shadow-lg p-4 sm:p-6 border border-white/20">
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">Garantías Incluidas</h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li className="flex items-center text-cyan-300">
                  <i className="ri-check-circle-line mr-2 flex-shrink-0"></i>
                  <span>Acceso inmediato tras el pago</span>
                </li>
                <li className="flex items-center text-cyan-300">
                  <i className="ri-check-circle-line mr-2 flex-shrink-0"></i>
                  <span>Soporte técnico incluido</span>
                </li>
                <li className="flex items-center text-cyan-300">
                  <i className="ri-check-circle-line mr-2 flex-shrink-0"></i>
                  <span>Actualizaciones gratuitas por 1 año</span>
                </li>
                <li className="flex items-center text-cyan-300">
                  <i className="ri-check-circle-line mr-2 flex-shrink-0"></i>
                  <span>Certificados oficiales</span>
                </li>
              </ul>
            </div>

            {/* Contacto */}
            <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20">
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4">¿Necesitas Ayuda?</h3>
              <div className="space-y-2 text-xs sm:text-sm text-blue-200">
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2 text-cyan-400 flex-shrink-0"></i>
                  <span className="break-all">indefinhum@gmail.com</span>
                </p>
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2 text-cyan-400 flex-shrink-0"></i>
                  <span>+52 55 2369 8187</span>
                </p>
                <p className="flex items-center">
                  <i className="ri-time-line mr-2 text-cyan-400 flex-shrink-0"></i>
                  <span>Lun-Vie 9:00-18:00 hrs</span>
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
